import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuth } from "../../context/AuthContext";

const GustUser = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const { gustRegister, gustLogin, isGustRegistering, isGustLoggingIn } = useAuth();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError("");
    reset();
  };

  const onRegisterSubmit = async (data) => {
    try {
      await gustRegister(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } 
  };

  const onLoginSubmit = async (data) => {
    try {
      const response = await gustLogin(data);
      console.log(response);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } 
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
        py: { xs: 4, md: 8 },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 0% 0%, rgba(92,15,47,0.05) 0%, transparent 50%)",
          zIndex: 0,
        },
      }}
    >
      {/* Navigation Buttons */}
      <Box
        sx={{
          position: "absolute",
          top: 20,
          left: 20,
          zIndex: 10,
          display: "flex",
          gap: 2,
        }}
      >
        <IconButton
          onClick={() => navigate("/")}
          sx={{
            bgcolor: "rgba(255,255,255,0.9)",
            "&:hover": { bgcolor: "white" },
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <HomeIcon />
        </IconButton>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: "rgba(255,255,255,0.9)",
            "&:hover": { bgcolor: "white" },
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: "16px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                transition: "transform 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                },
              }}
            >
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  textAlign: "center",
                  mb: 3,
                  fontWeight: 700,
                  color: "#333",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -10,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "50px",
                    height: "3px",
                    background: "linear-gradient(90deg, #5c0f2f, #7d1f4f)",
                    borderRadius: "2px",
                  },
                }}
              >
                Guest Access
              </Typography>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTab-root': {
                      color: '#666',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                    },
                    '& .Mui-selected': {
                      color: '#5c0f2f',
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#5c0f2f',
                    },
                  }}
                >
                  <Tab label="Login as Guest" />
                  <Tab label="Register as Guest" />
                </Tabs>
              </Box>

              {error && (
                <Typography
                  color="error"
                  sx={{
                    textAlign: "center",
                    mb: 2,
                    p: 1,
                    bgcolor: "rgba(255, 0, 0, 0.1)",
                    borderRadius: "4px",
                  }}
                >
                  {error}
                </Typography>
              )}

              {/* Login Tab */}
              {activeTab === 0 && (
                <Box
                  component="form"
                  onSubmit={handleSubmit(onLoginSubmit)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    {...register("email", { required: "Email is required" })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#5c0f2f",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#5c0f2f",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#5c0f2f",
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    {...register("mobile", {
                      required: "Mobile number is required",
                    })}
                    error={!!errors.mobile}
                    helperText={errors.mobile?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#5c0f2f",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#5c0f2f",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#5c0f2f",
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isGustLoggingIn}
                    sx={{
                      mt: 2,
                      py: 1.5,
                      background: "linear-gradient(45deg, #5c0f2f, #7d1f4f)",
                      "&:hover": {
                        background: "linear-gradient(45deg, #4d0d28, #601233)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 15px rgba(92,15,47,0.3)",
                      },
                      "&:disabled": {
                        background: "linear-gradient(45deg, #5c0f2f, #7d1f4f)",
                        opacity: 0.7,
                      },
                      fontSize: "1.1rem",
                      textTransform: "none",
                      borderRadius: "8px",
                      transition: "all 0.3s",
                    }}
                  >
                    {isGustLoggingIn ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={20} sx={{ color: "white" }} />
                        Logging in...
                      </Box>
                    ) : (
                      "Login as Guest"
                    )}
                  </Button>
                </Box>
              )}

              {/* Register Tab */}
              {activeTab === 1 && (
                <Box
                  component="form"
                  onSubmit={handleSubmit(onRegisterSubmit)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    {...register("email", { required: "Email is required" })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#5c0f2f",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#5c0f2f",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#5c0f2f",
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    {...register("mobile", {
                      required: "Mobile number is required",
                    })}
                    error={!!errors.mobile}
                    helperText={errors.mobile?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#5c0f2f",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#5c0f2f",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#5c0f2f",
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isGustRegistering}
                    sx={{
                      mt: 2,
                      py: 1.5,
                      background: "linear-gradient(45deg, #5c0f2f, #7d1f4f)",
                      "&:hover": {
                        background: "linear-gradient(45deg, #4d0d28, #601233)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 15px rgba(92,15,47,0.3)",
                      },
                      "&:disabled": {
                        background: "linear-gradient(45deg, #5c0f2f, #7d1f4f)",
                        opacity: 0.7,
                      },
                      fontSize: "1.1rem",
                      textTransform: "none",
                      borderRadius: "8px",
                      transition: "all 0.3s",
                    }}
                  >
                    {isGustRegistering ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={20} sx={{ color: "white" }} />
                        Submitting...
                      </Box>
                    ) : (
                      "Register as Guest"
                    )}
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default GustUser;
